import { useMemo, useState } from "react";
import {
  MessageSquare, Send, Users, Clock, BarChart3, Plus,
  CheckCircle2, AlertCircle, Loader2, FileText, Search,
  TrendingUp, DollarSign, Zap, ChevronRight, MoreHorizontal
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  smsTemplates, audienceSegments, smsCampaigns, smsAnalytics,
  type SMSTemplate, type SMSCampaign,
} from "@/data/smsData";

const statusConfig: Record<SMSCampaign["status"], { label: string; icon: React.ElementType; className: string }> = {
  draft: { label: "Draft", icon: FileText, className: "bg-muted text-muted-foreground" },
  scheduled: { label: "Scheduled", icon: Clock, className: "bg-accent text-accent-foreground" },
  sending: { label: "Sending", icon: Loader2, className: "bg-chart-2/15 text-[hsl(199,89%,48%)]" },
  completed: { label: "Completed", icon: CheckCircle2, className: "bg-primary/10 text-primary" },
  failed: { label: "Failed", icon: AlertCircle, className: "bg-destructive/10 text-destructive" },
};

const categoryColors: Record<SMSTemplate["category"], string> = {
  payment: "bg-primary/10 text-primary",
  promotion: "bg-chart-4/15 text-[hsl(43,96%,40%)]",
  reminder: "bg-chart-2/15 text-[hsl(199,89%,48%)]",
  alert: "bg-destructive/10 text-destructive",
};

function CampaignCard({ campaign }: { campaign: SMSCampaign }) {
  const sc = statusConfig[campaign.status];
  const Icon = sc.icon;
  const deliveryPct = campaign.sent > 0 ? Math.round((campaign.delivered / campaign.sent) * 100) : 0;

  return (
    <Card className="rounded-2xl shadow-sm">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold truncate">{campaign.name}</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              <span className="font-medium text-foreground/80">Template:</span> {campaign.template} •{" "}
              <span className="font-medium text-foreground/80">Audience:</span> {campaign.audience}
            </div>
          </div>

          <Badge variant="secondary" className={cn("gap-1 shrink-0", sc.className)}>
            <Icon className={cn("h-3 w-3", campaign.status === "sending" && "animate-spin")} />
            {sc.label}
          </Badge>
        </div>

        <div className="rounded-xl border border-border bg-muted/20 p-3">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Delivery</span>
            <span className="font-mono text-foreground">{campaign.delivered.toLocaleString()}/{campaign.sent.toLocaleString()}</span>
          </div>
          <div className="mt-2">
            <Progress value={deliveryPct} className="h-2" />
          </div>
          <div className="mt-2 grid grid-cols-3 gap-2">
            <div className="rounded-lg bg-background border border-border p-2 text-center">
              <div className="text-[11px] text-muted-foreground">Sent</div>
              <div className="font-mono text-sm font-semibold">{campaign.sent.toLocaleString()}</div>
            </div>
            <div className="rounded-lg bg-background border border-border p-2 text-center">
              <div className="text-[11px] text-muted-foreground">Delivered</div>
              <div className="font-mono text-sm font-semibold">{campaign.delivered.toLocaleString()}</div>
            </div>
            <div className="rounded-lg bg-background border border-border p-2 text-center">
              <div className="text-[11px] text-muted-foreground">Failed</div>
              <div className="font-mono text-sm font-semibold text-destructive">{campaign.failed.toLocaleString()}</div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground/80">Scheduled:</span> {campaign.scheduledAt || "—"}
          </div>
          <Button variant="ghost" size="sm" className="gap-1">
            Details <ChevronRight className="h-3 w-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

export default function BulkSMSPage () {
  const [activeTab, setActiveTab] = useState("campaigns");
  const [searchQuery, setSearchQuery] = useState("");
  const [composeOpen, setComposeOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [selectedAudience, setSelectedAudience] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [messageContent, setMessageContent] = useState("");

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplate(templateId);
    const template = smsTemplates.find((t) => t.id === templateId);
    if (template) setMessageContent(template.content);
  };

  const filteredCampaigns = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return smsCampaigns;
    return smsCampaigns.filter((c) => c.name.toLowerCase().includes(q));
  }, [searchQuery]);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-30">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center shrink-0">
              <MessageSquare className="h-5 w-5 text-primary-foreground" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-foreground tracking-tight truncate">Bulk SMS</h1>
              <p className="text-xs text-muted-foreground truncate">Campaign Manager</p>
            </div>
          </div>

          <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2 shrink-0">
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">New Campaign</span>
                <span className="sm:hidden">New</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>Create New Campaign</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="space-y-2">
                  <Label>Campaign Name</Label>
                  <Input
                    placeholder="e.g. March Payment Reminders"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Template</Label>
                    <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
                      <SelectTrigger><SelectValue placeholder="Choose template" /></SelectTrigger>
                      <SelectContent>
                        {smsTemplates.map((t) => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Audience</Label>
                    <Select value={selectedAudience} onValueChange={setSelectedAudience}>
                      <SelectTrigger><SelectValue placeholder="Select audience" /></SelectTrigger>
                      <SelectContent>
                        {audienceSegments.map((a) => (
                          <SelectItem key={a.id} value={a.id}>
                            {a.name} ({a.count.toLocaleString()})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Message</Label>
                    <span className="text-xs text-muted-foreground font-mono">{messageContent.length}/160 chars</span>
                  </div>
                  <Textarea
                    rows={4}
                    placeholder="Type your message or select a template..."
                    value={messageContent}
                    onChange={(e) => setMessageContent(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Schedule</Label>
                  <Input type="datetime-local" />
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button variant="outline" className="flex-1" onClick={() => setComposeOpen(false)}>
                    Save as Draft
                  </Button>
                  <Button className="flex-1 gap-2">
                    <Send className="h-4 w-4" />
                    Schedule Campaign
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 space-y-6">
        {/* Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="rounded-2xl">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">Total Sent</p>
                  <p className="text-2xl font-bold text-foreground mt-1">{smsAnalytics.totalSent.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground mt-1">This period</p>
                </div>
                <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Send className="h-5 w-5 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">Delivery Rate</p>
                  <p className="text-2xl font-bold text-foreground mt-1">{smsAnalytics.deliveryRate}%</p>
                  <p className="text-xs text-primary mt-1 flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" /> +2.1% vs last month
                  </p>
                </div>
                <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">Response Rate</p>
                  <p className="text-2xl font-bold text-foreground mt-1">{smsAnalytics.avgResponseRate}%</p>
                  <p className="text-xs text-muted-foreground mt-1">Average engagement</p>
                </div>
                <div className="h-11 w-11 rounded-xl bg-accent flex items-center justify-center">
                  <Zap className="h-5 w-5 text-accent-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">Total Cost</p>
                  <p className="text-2xl font-bold text-foreground mt-1">KES {smsAnalytics.totalCost.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground mt-1">@ KES {smsAnalytics.costPerSMS}/SMS</p>
                </div>
                <div className="h-11 w-11 rounded-xl bg-accent flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-accent-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* ✅ Responsive tabs: scroll on mobile */}
          <div className="overflow-x-auto">
            <TabsList className="bg-card border border-border w-max min-w-full sm:w-auto">
              <TabsTrigger value="campaigns" className="gap-2"><Send className="h-4 w-4" />Campaigns</TabsTrigger>
              <TabsTrigger value="templates" className="gap-2"><FileText className="h-4 w-4" />Templates</TabsTrigger>
              <TabsTrigger value="audiences" className="gap-2"><Users className="h-4 w-4" />Audiences</TabsTrigger>
              <TabsTrigger value="analytics" className="gap-2"><BarChart3 className="h-4 w-4" />Analytics</TabsTrigger>
            </TabsList>
          </div>

          {/* Campaigns Tab */}
          <TabsContent value="campaigns" className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="relative flex-1 sm:max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search campaigns..."
                  className="pl-9"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Button variant="outline" className="sm:ml-auto gap-2">
                <MoreHorizontal className="h-4 w-4" />
                Actions
              </Button>
            </div>

            {/* ✅ Desktop/Tablet Table */}
            <Card className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Template</TableHead>
                    <TableHead>Audience</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Sent</TableHead>
                    <TableHead className="text-right">Delivered</TableHead>
                    <TableHead className="text-right">Failed</TableHead>
                    <TableHead>Scheduled</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCampaigns.map((campaign) => {
                    const sc = statusConfig[campaign.status];
                    const Icon = sc.icon;
                    return (
                      <TableRow key={campaign.id}>
                        <TableCell className="font-medium">{campaign.name}</TableCell>
                        <TableCell className="text-muted-foreground">{campaign.template}</TableCell>
                        <TableCell className="text-muted-foreground">{campaign.audience}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className={`gap-1 ${sc.className}`}>
                            <Icon className={`h-3 w-3 ${campaign.status === "sending" ? "animate-spin" : ""}`} />
                            {sc.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">{campaign.sent.toLocaleString()}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{campaign.delivered.toLocaleString()}</TableCell>
                        <TableCell className="text-right font-mono text-sm text-destructive">{campaign.failed.toLocaleString()}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{campaign.scheduledAt || "—"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>

            {/* ✅ Mobile Cards */}
            <div className="grid grid-cols-1 gap-4 md:hidden">
              {filteredCampaigns.map((c) => (
                <CampaignCard key={c.id} campaign={c} />
              ))}
            </div>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {smsTemplates.map((template) => (
                <Card key={template.id} className="hover:shadow-md transition-shadow rounded-2xl">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-3">
                      <CardTitle className="text-base truncate">{template.name}</CardTitle>
                      <Badge variant="secondary" className={categoryColors[template.category]}>
                        {template.category}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground leading-relaxed font-mono bg-muted/50 rounded-lg p-3">
                      {template.content}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">{template.charCount} characters</span>
                      <Button variant="ghost" size="sm" className="gap-1 text-primary">
                        Use Template <ChevronRight className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Audiences Tab */}
          <TabsContent value="audiences" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {audienceSegments.map((segment) => (
                <Card key={segment.id} className="hover:shadow-md transition-shadow rounded-2xl">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3 gap-3">
                      <div
                        className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
                        style={{ backgroundColor: `${segment.color}20` }}
                      >
                        <Users className="h-5 w-5" style={{ color: segment.color }} />
                      </div>
                      <span className="text-2xl font-bold text-foreground">{segment.count.toLocaleString()}</span>
                    </div>
                    <h3 className="font-semibold text-foreground">{segment.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{segment.description}</p>
                    <div className="mt-4">
                      <Button variant="outline" size="sm" className="w-full gap-2">
                        <Send className="h-3 w-3" /> Send to Segment
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="rounded-2xl">
                <CardHeader>
                  <CardTitle className="text-base">Delivery Trends</CardTitle>
                  <CardDescription>Sent vs delivered over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={smsAnalytics.monthlyData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(220, 13%, 90%)" />
                        <XAxis dataKey="month" tick={{ fontSize: 12, fill: "hsl(220, 10%, 46%)" }} />
                        <YAxis tick={{ fontSize: 12, fill: "hsl(220, 10%, 46%)" }} />
                        <Tooltip contentStyle={{ borderRadius: "0.75rem", border: "1px solid hsl(220, 13%, 90%)", fontSize: 13 }} />
                        <Legend />
                        <Area type="monotone" dataKey="sent" name="Sent" stackId="1" stroke="hsl(173, 58%, 39%)" fill="hsl(173, 58%, 39%)" fillOpacity={0.15} />
                        <Area type="monotone" dataKey="delivered" name="Delivered" stackId="2" stroke="hsl(199, 89%, 48%)" fill="hsl(199, 89%, 48%)" fillOpacity={0.15} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-2xl">
                <CardHeader>
                  <CardTitle className="text-base">Monthly Cost</CardTitle>
                  <CardDescription>SMS expenditure in KES</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={smsAnalytics.monthlyData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(220, 13%, 90%)" />
                        <XAxis dataKey="month" tick={{ fontSize: 12, fill: "hsl(220, 10%, 46%)" }} />
                        <YAxis tick={{ fontSize: 12, fill: "hsl(220, 10%, 46%)" }} />
                        <Tooltip contentStyle={{ borderRadius: "0.75rem", border: "1px solid hsl(220, 13%, 90%)", fontSize: 13 }} />
                        <Bar dataKey="cost" name="Cost (KES)" fill="hsl(173, 58%, 39%)" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Delivery breakdown */}
            <Card className="rounded-2xl">
              <CardHeader>
                <CardTitle className="text-base">Overall Delivery Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Delivered</span>
                    <span className="font-mono font-medium text-foreground">
                      {smsAnalytics.totalDelivered.toLocaleString()} ({smsAnalytics.deliveryRate}%)
                    </span>
                  </div>
                  <Progress value={smsAnalytics.deliveryRate} className="h-3" />
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                    <div className="text-center p-3 rounded-lg bg-primary/5">
                      <p className="text-lg font-bold text-primary">{smsAnalytics.totalSent.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Total Sent</p>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-accent">
                      <p className="text-lg font-bold text-accent-foreground">{smsAnalytics.totalDelivered.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Delivered</p>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-destructive/5">
                      <p className="text-lg font-bold text-destructive">{smsAnalytics.totalFailed.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Failed</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

